import { DropdownButton, EllipsisDropdownItem } from "@similarweb/ui-components";
import type { FC } from "react";
import { translate } from "app/filters/i18nFilter";
import type { IRTCurrentCampaignData } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { MultiSelectDropdownContainer } from "app/components/dashboard/widget-wizard/components/MultiSelectDropdownContainer";
import { EllipsisDropdownItemStyled } from "app/pages/digital-marketing/brand-monitoring-campaigns/shared/filters/BrandMonitoringCompetitorsFilter";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import {
    CompetitorsDropdownContainer,
    OptionalTagWrapper,
    SectionTitle,
} from "app/components/dashboard/widget-wizard/components/campaigns/CampaignsDashboardWizardStyled";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { noop } from "lodash";

export type ICampaignCompetitor = {
    id: string;
    name: string;
    image: string;
};

type CompetitorsDropdownProps = {
    selectedSites: ICampaignCompetitor[];
    currentCampaignConfiguration: IRTCurrentCampaignData | null;
    keyAppend: (competitor: ICampaignCompetitor) => void;
    keyRemove: (competitor: ICampaignCompetitor) => void;
    onDropDownToggle?: (isOpen: boolean) => void;
};

const getSelectedCompetitorsFromCampaign = (selectedSites: ICampaignCompetitor[]) => {
    return selectedSites?.reduce((obj, site) => {
        obj[site.name] = true;
        return obj;
    }, {});
};
export const CompetitorsDropdown: FC<CompetitorsDropdownProps> = ({
    selectedSites,
    currentCampaignConfiguration,
    keyAppend,
    keyRemove,
    onDropDownToggle = noop,
}) => {
    const selectedCompetitorsIds = getSelectedCompetitorsFromCampaign(selectedSites);

    const itemSelected = (selectedItems) => {
        const [changedKeysWithValue] = Object.keys(selectedItems)
            .filter((key) => selectedCompetitorsIds[key] !== selectedItems[key])
            .map((key) => ({ competitorKey: key, isAppend: selectedItems[key] }));
        const { competitorKey, isAppend } = changedKeysWithValue;
        if (!currentCampaignConfiguration) {
            return;
        }
        const [competitorToChange] = currentCampaignConfiguration.competitors
            .filter((com) => com.site === competitorKey)
            .map((com) => ({ id: com.site, name: com.site, image: com.asset }));
        if (isAppend) {
            keyAppend(competitorToChange);
        } else {
            keyRemove(competitorToChange);
        }
    };

    const competitorsToRender = currentCampaignConfiguration?.competitors
        ? [
              ...currentCampaignConfiguration.competitors?.map((competitor) => (
                  <EllipsisDropdownItemStyled
                      key={competitor.site}
                      id={competitor.site}
                      imageUrl={competitor.asset}
                      showCheckBox={competitor.site !== currentCampaignConfiguration?.site}
                  >
                      {competitor.site}
                  </EllipsisDropdownItemStyled>
              )),
          ]
        : [];

    const getHeaderComponent = () => {
        const selectedItems = Object.entries(selectedCompetitorsIds).filter(([_, value]) => !!value);
        const amountOfSelectedSites = selectedItems.length;
        let selectedFirstCompetitor;
        if (amountOfSelectedSites === 1) {
            const [selectedSite] = selectedItems[0];
            selectedFirstCompetitor = currentCampaignConfiguration?.competitors?.find(
                (competitor) => competitor.site === selectedSite,
            );
            return (
                <DropdownButton>
                    {/*<EllipsisDropdownItemStyled*/}
                    <EllipsisDropdownItem
                        key={selectedFirstCompetitor?.site}
                        id={selectedFirstCompetitor?.site}
                        imageUrl={selectedFirstCompetitor?.asset}
                    >
                        {selectedFirstCompetitor?.site}
                    </EllipsisDropdownItem>
                </DropdownButton>
            );
        }
        return amountOfSelectedSites > 1 ? (
            <DropdownButton>
                {translate("seo.keywordsCampaigns.dashboards.competitorsFilter.selected", {
                    amount: amountOfSelectedSites,
                })}
            </DropdownButton>
        ) : (
            <DropdownButton disabled={!currentCampaignConfiguration}>
                {translate("seo.keywordsCampaigns.dashboards.competitorsFilter.placeholder")}
            </DropdownButton>
        );
    };

    return (
        <>
            <FlexRow>
                <SectionTitle>
                    <I18n>home.dashboards.wizard.competitors.title</I18n>
                </SectionTitle>
                <OptionalTagWrapper>
                    <I18n>home.dashboards.wizard.competitors.optional</I18n>
                </OptionalTagWrapper>
            </FlexRow>
            <CompetitorsDropdownContainer>
                <MultiSelectDropdownContainer
                    onItemClick={itemSelected}
                    initialSelection={selectedCompetitorsIds}
                    disabled={!currentCampaignConfiguration}
                    onToggle={onDropDownToggle}
                >
                    {[getHeaderComponent(), ...competitorsToRender]}
                </MultiSelectDropdownContainer>
            </CompetitorsDropdownContainer>
        </>
    );
};
